export interface LegendEntry {
  id: string;
  label: string;
  sub?: string | null;
  color: string;
  dashed?: boolean;
  visible: boolean;
  disabled?: boolean;
}

export interface LegendGroup {
  /** Stable key, e.g. "month" / "quarter" / "year". */
  key: string;
  /** Group heading, e.g. "Monate". */
  label: string;
  entries: readonly LegendEntry[];
  /** True when every entry in the group is visible. */
  allVisible: boolean;
  /** True when at least one entry is visible. */
  anyVisible: boolean;
}

interface Props {
  /** Flat list of entries (used by the spot / generation charts). */
  entries?: readonly LegendEntry[];
  /** Grouped entries (used by the futures-history chart). Takes precedence. */
  groups?: readonly LegendGroup[];
  onToggle: (id: string) => void;
  /** Toggle a whole group on/off. Required when `groups` is provided. */
  onToggleGroup?: (key: string) => void;
}

function EntryButton({ e, onToggle }: { e: LegendEntry; onToggle: (id: string) => void }) {
  const visibleClass = e.visible
    ? "text-slate-700 dark:text-slate-200"
    : "text-slate-400 dark:text-slate-500 line-through";
  return (
    <li>
      <button
        type="button"
        onClick={() => onToggle(e.id)}
        disabled={e.disabled}
        className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:cursor-not-allowed disabled:opacity-50 ${visibleClass}`}
        aria-pressed={e.visible}
      >
        <span
          className="inline-block w-4 h-0 border-t-2"
          style={{
            borderColor: e.color,
            borderStyle: e.dashed ? "dashed" : "solid",
            opacity: e.visible ? 1 : 0.4,
          }}
        />
        <span className="font-medium whitespace-nowrap">{e.label}</span>
        {e.sub && (
          <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">{e.sub}</span>
        )}
      </button>
    </li>
  );
}

export function ChartLegend({ entries, groups, onToggle, onToggleGroup }: Props) {
  // Grouped mode: one block per tenor (Monate / Quartale / Jahre) with a
  // group-level show/hide toggle plus the individual contract entries.
  if (groups) {
    return (
      <div className="flex flex-col gap-2">
        {groups.map((g) => (
          <div key={g.key} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
            <button
              type="button"
              onClick={() => onToggleGroup?.(g.key)}
              className={`px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide transition hover:bg-slate-100 dark:hover:bg-slate-800 ${
                g.anyVisible
                  ? "text-slate-600 dark:text-slate-300"
                  : "text-slate-400 dark:text-slate-500"
              }`}
              aria-pressed={g.allVisible}
              title={g.allVisible ? "Gruppe ausblenden" : "Gruppe einblenden"}
            >
              {g.label}
            </button>
            <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
              {g.entries.map((e) => (
                <EntryButton key={e.id} e={e} onToggle={onToggle} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
      {(entries ?? []).map((e) => (
        <EntryButton key={e.id} e={e} onToggle={onToggle} />
      ))}
    </ul>
  );
}
